import { useCallback, useEffect, useState } from 'react';
import axios from '../../../../axiosConfig';
import { UserPreferences } from './types';

export type PreferencesState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; prefs: UserPreferences };

/**
 * The signed-in user's own preferences. Loaded once by the page so the Preferences card and the
 * times on the Interviews card agree. `save` sends the whole new state and resolves with what
 * the server stored; it rejects on failure so the caller can say so.
 */
export const usePreferences = (enabled: boolean) => {
  const [state, setState] = useState<PreferencesState>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    setState({ status: 'loading' });
    axios
      .get<UserPreferences>('/api/users/me/preferences')
      .then((res) => {
        if (!cancelled) setState({ status: 'ready', prefs: res.data });
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, attempt]);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  const save = useCallback(async (next: UserPreferences) => {
    const res = await axios.put<UserPreferences>(
      '/api/users/me/preferences',
      next,
    );
    setState({ status: 'ready', prefs: res.data });
    return res.data;
  }, []);

  return { state, reload, save };
};
